import { t } from "../i18n"
import { Controller } from "@hotwired/stimulus"

// Enhances Trix editor:
// - Adds underline formatting and button
// - Removes unused toolbar groups/buttons
// - Replaces icons with custom SVGs
// - Disables file uploads via the editor (use dedicated upload UI instead)
// - Enhanced button styling with pressed states
export default class extends Controller {
  static values = {
    allowAttachments: { type: Boolean, default: true }
  }

  static UNUSED_TOOLBAR_SELECTORS = [
    ".trix-button--icon-strike",
    ".trix-button--icon-link",
    ".trix-button-group--block-tools",
    ".trix-button-group--history-tools",
    ".trix-button--icon-decrease-nesting-level",
    ".trix-button--icon-increase-nesting-level"
  ]

  static TOOLBAR_BUTTON_ICONS = [
    { selector: ".trix-button--icon-bold",       svg: trixIconBold() },
    { selector: ".trix-button--icon-italic",     svg: trixIconItalic() },
    { selector: ".trix-button--icon-underline",  svg: trixIconUnderline() },
    { selector: ".trix-button--icon-bullet-list",svg: trixIconBulletedList() },
    { selector: ".trix-button--icon-number-list",svg: trixIconNumberedList() },
    { selector: ".trix-button--icon-attach",     svg: trixIconAttach() }
  ]

  connect() {
    // Gate file uploads in Trix editors managed by this controller.
    this._onFileAccept = (event) => {
      if (!this.allowAttachmentsValue) {
        event.preventDefault()
      }
    }

    // Initialize once Trix attaches editor + toolbar to the DOM
    this._onInitialize = (event) => {
      const editorElement = event.target
      if (editorElement !== this.element) return // Only act on our instance

      const toolbar = editorElement.toolbarElement
      if (!toolbar) return

      this.ensureUnderlineAttribute()
      this.injectUnderlineButton(toolbar)
      this.removeUnusedTools(toolbar)
      this.swapToolbarIcons(toolbar)
      this.enhanceA11y(toolbar)
      this.updatePressedStates(toolbar)
      this.addButtonInteractivity(toolbar)

      // Keep aria-pressed in sync when selection or formatting changes
      this._onSelectionChange = (e) => {
        if (e.target !== editorElement) return
        this.updatePressedStates(toolbar)
      }
      addEventListener("trix-selection-change", this._onSelectionChange, true)
      addEventListener("trix-change", this._onSelectionChange, true)
    }

    addEventListener("trix-file-accept", this._onFileAccept, true)
    addEventListener("trix-initialize", this._onInitialize, true)
  }

  disconnect() {
    removeEventListener("trix-file-accept", this._onFileAccept, true)
    removeEventListener("trix-initialize", this._onInitialize, true)
    if (this._onSelectionChange) {
      removeEventListener("trix-selection-change", this._onSelectionChange, true)
      removeEventListener("trix-change", this._onSelectionChange, true)
    }
  }

  ensureUnderlineAttribute() {
    if (window.Trix && !Trix.config.textAttributes.underline) {
      Trix.config.textAttributes.underline = {
        tagName: "u",
        style: { textDecoration: "underline" },
        inheritable: true,
        parser(element) {
          try {
            const style = window.getComputedStyle(element)
            return style.textDecorationLine?.includes("underline") || style.textDecoration === "underline"
          } catch (_) {
            return false
          }
        }
      }
    }
  }

  injectUnderlineButton(toolbar) {
    if (!toolbar) return
    // If already present, skip (e.g., Turbo cache or multiple connects)
    if (toolbar.querySelector(".trix-button--icon-underline")) return

    const textToolsGroup = toolbar.querySelector(".trix-button-group--text-tools")
    if (!textToolsGroup) return

    const btn = document.createElement("button")
    btn.type = "button"
    btn.className = "trix-button trix-button--icon trix-button--icon-underline"
    btn.setAttribute("data-trix-attribute", "underline")
    btn.setAttribute("data-trix-key", "u")
    btn.setAttribute("tabindex", -1)
    const underlineLabel = t("javascript.controllers.trix_controller.underline")
    btn.setAttribute("title", underlineLabel)
    btn.setAttribute("aria-label", underlineLabel)
    // Initial innerHTML set to text; will be replaced with SVG below
    btn.innerHTML = "U"

    textToolsGroup.appendChild(btn)
  }

  removeUnusedTools(toolbar) {
    this.constructor.UNUSED_TOOLBAR_SELECTORS.forEach((sel) => {
      toolbar.querySelectorAll(sel).forEach((el) => el?.remove())
    })

    // Remove attachment button if attachments are disabled
    if (!this.allowAttachmentsValue) {
      toolbar.querySelectorAll(".trix-button--icon-attach, .trix-button-group--file-tools").forEach((el) => el?.remove())
    }
  }

  swapToolbarIcons(toolbar) {
    // Remove default background images from buttons with icons; CSS also handles this, but ensure here too
    toolbar.querySelectorAll(".trix-button--icon").forEach((btn) => {
      btn.style.backgroundImage = "none"
    })

    this.constructor.TOOLBAR_BUTTON_ICONS.forEach(({ selector, svg }) => {
      const el = toolbar.querySelector(selector)
      if (el) el.innerHTML = svg
    })
  }

  enhanceA11y(toolbar) {
    // Ensure focus ring and descriptive labels are present
    toolbar.querySelectorAll(".trix-button").forEach((btn) => {
      if (!btn.getAttribute("aria-label") && btn.getAttribute("title")) {
        btn.setAttribute("aria-label", btn.getAttribute("title"))
      }
    })
  }

  addButtonInteractivity(toolbar) {
    // Add mouse events for better visual feedback - HANDLED BY CSS NOW
  }

  updatePressedStates(toolbar) {
    // Sync aria-pressed with Trix's .trix-active class for toggle buttons
    toolbar.querySelectorAll(".trix-button").forEach((btn) => {
      const isActive = btn.classList.contains("trix-active")
      btn.setAttribute("aria-pressed", isActive ? "true" : "false")
    })
  }
}

// --- Icons ---
function trixIconBold() {
  return `
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <path d="M6 4h8a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z"></path>
      <path d="M6 12h9a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z"></path>
    </svg>
  `
}

function trixIconItalic() {
  return `
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <line x1="19" y1="4" x2="10" y2="4"></line>
      <line x1="14" y1="20" x2="5" y2="20"></line>
      <line x1="15" y1="4" x2="9" y2="20"></line>
    </svg>
  `
}

function trixIconUnderline() {
  return `
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <path d="M6 3v7a6 6 0 0 0 6 6 6 6 0 0 0 6-6V3"></path>
      <line x1="4" y1="21" x2="20" y2="21"></line>
    </svg>
  `
}

function trixIconBulletedList() {
  return `
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <line x1="8" y1="6" x2="21" y2="6"></line>
      <line x1="8" y1="12" x2="21" y2="12"></line>
      <line x1="8" y1="18" x2="21" y2="18"></line>
      <line x1="3" y1="6" x2="3.01" y2="6"></line>
      <line x1="3" y1="12" x2="3.01" y2="12"></line>
      <line x1="3" y1="18" x2="3.01" y2="18"></line>
    </svg>
  `
}

function trixIconNumberedList() {
  return `
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <line x1="10" y1="6" x2="21" y2="6"></line>
      <line x1="10" y1="12" x2="21" y2="12"></line>
      <line x1="10" y1="18" x2="21" y2="18"></line>
      <path d="M4 6h1v4"></path>
      <path d="M4 10h2"></path>
      <path d="M6 18H4c0-1 2-2 2-3s-1-1.5-2-1"></path>
    </svg>
  `
}

function trixIconAttach() {
  return `
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"></path>
    </svg>
  `
}
